import { type BonusKind, bonusX, type FallingBonus, isNegative } from './bonuses'
import { gameConfig, WORLD_HEIGHT, WORLD_WIDTH } from './config'

/**
 * Жетоны бонусов. Источник — те же бейджи, что и в листе ассетов Gemini
 * (ячейки buff-*.png / debuff-*.png / coin.png): хорошие — светлый скруглённый
 * квадрат с тёмной пиктограммой, отрицательные — коралловый ромб с белой.
 * Форма отличается, а не только цвет, — чтобы их различали и при дальтонизме.
 * PNG того же каталога, что у ракетки и волана, и по той же причине (см.
 * racket.ts) — не SVG напрямую в canvas.
 */

type GoodKind = 'wide' | 'slow' | 'double' | 'shield'
type BadKind = 'narrow' | 'fast' | 'fog' | 'breaker'

/** Единый цвет пиктограммы на белой плашке — для запасной отрисовки до загрузки картинки. */
const GOOD_ICON = '#16324f'
/** Заливка дебафф-ромба — токен --bad из Game.css (Coral Red по спеке). */
const BAD_FILL = '#c0432f'
const BAD_ICON = '#ffffff'

const ICON_FILES: Record<BonusKind, { url: string; srcW: number; srcH: number }> = {
  coin: { url: '/game/bonus-coin.png', srcW: 274, srcH: 276 },
  wide: { url: '/game/bonus-wide.png', srcW: 298, srcH: 296 },
  slow: { url: '/game/bonus-slow.png', srcW: 300, srcH: 296 },
  double: { url: '/game/bonus-double.png', srcW: 300, srcH: 294 },
  shield: { url: '/game/bonus-shield.png', srcW: 300, srcH: 294 },
  narrow: { url: '/game/bonus-narrow.png', srcW: 302, srcH: 304 },
  fast: { url: '/game/bonus-fast.png', srcW: 306, srcH: 304 },
  fog: { url: '/game/bonus-fog.png', srcW: 304, srcH: 306 },
  breaker: { url: '/game/bonus-breaker.png', srcW: 304, srcH: 304 },
}

const icons: Partial<Record<BonusKind, HTMLImageElement>> = {}
const iconsReady: Partial<Record<BonusKind, boolean>> = {}
if (typeof Image !== 'undefined') {
  for (const kind of Object.keys(ICON_FILES) as BonusKind[]) {
    const img = new Image()
    img.onload = () => {
      iconsReady[kind] = true
    }
    img.src = ICON_FILES[kind].url
    icons[kind] = img
  }
}

export function drawBonuses(ctx: CanvasRenderingContext2D, bonuses: FallingBonus[]): void {
  for (const b of bonuses) drawBonus(ctx, b.kind, bonusX(b), b.y, b.age)
}

export function drawBonus(
  ctx: CanvasRenderingContext2D,
  kind: BonusKind,
  x: number,
  y: number,
  age: number,
): void {
  const r = gameConfig.bonus.radius
  // Лёгкая пульсация — бонус должен цеплять взгляд на фоне корта.
  const pulse = 1 + Math.sin(age * 5) * 0.05

  ctx.save()
  ctx.translate(x, y)
  ctx.scale(pulse, pulse)

  const halo = ctx.createRadialGradient(0, 0, r * 0.8, 0, 0, r * 1.7)
  halo.addColorStop(0, 'rgba(255, 255, 255, 0.45)')
  halo.addColorStop(1, 'rgba(255, 255, 255, 0)')
  ctx.fillStyle = halo
  ctx.beginPath()
  ctx.arc(0, 0, r * 1.7, 0, Math.PI * 2)
  ctx.fill()

  ctx.shadowColor = 'rgba(30, 50, 80, 0.3)'
  ctx.shadowBlur = 5
  ctx.shadowOffsetY = 2

  const file = ICON_FILES[kind]
  const img = icons[kind]
  if (img && iconsReady[kind]) {
    // Картинка целиком (плашка + пиктограмма) по диаметру 2r — тот же
    // габарит, что был у монеты в векторной версии.
    const k = (2 * r) / file.srcW
    const w = file.srcW * k
    const h = file.srcH * k
    ctx.drawImage(img, -w / 2, -h / 2, w, h)
  } else if (kind === 'coin') {
    drawCoin(ctx, r)
  } else if (isNegative(kind)) {
    drawBadBadge(ctx, r, kind as BadKind)
  } else {
    drawBadge(ctx, r, kind as GoodKind)
  }

  ctx.restore()
}

function drawCoin(ctx: CanvasRenderingContext2D, r: number): void {
  const face = ctx.createRadialGradient(-r * 0.35, -r * 0.4, r * 0.1, 0, 0, r)
  face.addColorStop(0, '#fff1bf')
  face.addColorStop(0.55, '#f0c95a')
  face.addColorStop(1, '#c9982c')
  ctx.fillStyle = face
  ctx.beginPath()
  ctx.arc(0, 0, r, 0, Math.PI * 2)
  ctx.fill()
  ctx.shadowColor = 'transparent'

  ctx.strokeStyle = 'rgba(150, 105, 20, 0.55)'
  ctx.lineWidth = 1.4
  ctx.beginPath()
  ctx.arc(0, 0, r * 0.72, 0, Math.PI * 2)
  ctx.stroke()

  // Чеканка — волан в профиль.
  ctx.fillStyle = 'rgba(150, 105, 20, 0.6)'
  ctx.beginPath()
  ctx.arc(0, -r * 0.28, r * 0.17, 0, Math.PI * 2)
  ctx.moveTo(-r * 0.12, -r * 0.14)
  ctx.lineTo(-r * 0.32, r * 0.36)
  ctx.lineTo(r * 0.32, r * 0.36)
  ctx.lineTo(r * 0.12, -r * 0.14)
  ctx.fill()
}

/** Контур скруглённого квадрата по центру в локальных координатах. */
function roundedSquarePath(ctx: CanvasRenderingContext2D, half: number, radius: number): void {
  ctx.beginPath()
  ctx.moveTo(-half + radius, -half)
  ctx.lineTo(half - radius, -half)
  ctx.arcTo(half, -half, half, -half + radius, radius)
  ctx.lineTo(half, half - radius)
  ctx.arcTo(half, half, half - radius, half, radius)
  ctx.lineTo(-half + radius, half)
  ctx.arcTo(-half, half, -half, half - radius, radius)
  ctx.lineTo(-half, -half + radius)
  ctx.arcTo(-half, -half, -half + radius, -half, radius)
  ctx.closePath()
}

/**
 * Ракетка-пиктограмма для жетона «узкая ракетка»: овальная голова со
 * струнами-засечками (без них на таком размере читается как лупа) и короткая
 * рукоять впритык к голове, без зазора.
 */
function drawRacketIcon(ctx: CanvasRenderingContext2D, g: number): void {
  ctx.save()
  ctx.translate(-g * 0.06, -g * 0.24)
  ctx.rotate(-0.5)
  const headW = g * 0.5
  const headH = g * 0.62
  ctx.lineWidth = g * 0.14
  ctx.beginPath()
  ctx.ellipse(0, -headH * 0.58, headW, headH, 0, 0, Math.PI * 2)
  ctx.stroke()
  // Крест-накрест внутри головы — сразу читается как струны, не как лупа.
  ctx.save()
  ctx.beginPath()
  ctx.ellipse(0, -headH * 0.58, headW * 0.78, headH * 0.78, 0, 0, Math.PI * 2)
  ctx.clip()
  ctx.lineWidth = g * 0.06
  ctx.beginPath()
  for (const t of [-0.55, 0, 0.55]) {
    ctx.moveTo(-headW, -headH * 0.58 + t * headH)
    ctx.lineTo(headW, -headH * 0.58 + t * headH)
  }
  for (const t of [-0.5, 0.5]) {
    ctx.moveTo(t * headW, -headH * 1.6)
    ctx.lineTo(t * headW, headH * 0.4)
  }
  ctx.stroke()
  ctx.restore()
  // Рукоять — встык с головой, без разрыва.
  ctx.lineWidth = g * 0.18
  ctx.beginPath()
  ctx.moveTo(0, -headH * 0.02)
  ctx.lineTo(0, g * 0.82)
  ctx.stroke()
  ctx.restore()
}

function drawBadge(ctx: CanvasRenderingContext2D, r: number, kind: GoodKind): void {
  const half = r * 0.86
  const radius = half * 0.38
  roundedSquarePath(ctx, half, radius)
  ctx.fillStyle = '#ffffff'
  ctx.fill()
  ctx.shadowColor = 'transparent'

  ctx.fillStyle = GOOD_ICON
  ctx.strokeStyle = GOOD_ICON
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  const g = r * 0.5
  switch (kind) {
    case 'wide':
      // В референсе тут ракетка + мелкий текст «×1.5», но на реальном размере
      // жетона (радиус игровой, не постер) текст такой мелкий, что
      // антиалиасинг съедает его совсем — см. обсуждение в PR. Оставляем
      // крупный множитель, тот же приём, что и у double — пара читается как
      // одно семейство «×N».
      ctx.font = `${Math.round(r * 0.95)}px 'Russo One', 'Manrope', system-ui, sans-serif`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(`×${gameConfig.bonus.wideFactor}`, 0, 1)
      return
    case 'slow':
      // Песочные часы.
      ctx.lineWidth = g * 0.17
      ctx.beginPath()
      ctx.moveTo(-g * 0.42, -g * 0.65)
      ctx.lineTo(g * 0.42, -g * 0.65)
      ctx.lineTo(g * 0.08, 0)
      ctx.lineTo(g * 0.42, g * 0.65)
      ctx.lineTo(-g * 0.42, g * 0.65)
      ctx.lineTo(-g * 0.08, 0)
      ctx.closePath()
      ctx.stroke()
      ctx.fillStyle = GOOD_ICON
      ctx.beginPath()
      ctx.moveTo(-g * 0.22, -g * 0.42)
      ctx.lineTo(g * 0.22, -g * 0.42)
      ctx.lineTo(0, -g * 0.05)
      ctx.closePath()
      ctx.fill()
      return
    case 'double':
      // Тот же дисплейный шрифт, что и у лент HUD: канвас видит @font-face страницы.
      ctx.font = `${Math.round(r * 0.95)}px 'Russo One', 'Manrope', system-ui, sans-serif`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText('×2', 0, 1)
      return
    case 'shield':
      ctx.beginPath()
      ctx.moveTo(0, -g)
      ctx.quadraticCurveTo(g * 0.9, -g * 0.8, g * 0.85, -g * 0.2)
      ctx.quadraticCurveTo(g * 0.7, g * 0.7, 0, g)
      ctx.quadraticCurveTo(-g * 0.7, g * 0.7, -g * 0.85, -g * 0.2)
      ctx.quadraticCurveTo(-g * 0.9, -g * 0.8, 0, -g)
      ctx.fill()
      return
  }
}

function drawBadBadge(ctx: CanvasRenderingContext2D, r: number, kind: BadKind): void {
  // Ромб чуть крупнее квадрата той же площади — иначе выглядит мельче.
  const d = r * 1.08
  const diamond = () => {
    ctx.beginPath()
    ctx.moveTo(0, -d)
    ctx.lineTo(d, 0)
    ctx.lineTo(0, d)
    ctx.lineTo(-d, 0)
    ctx.closePath()
  }
  ctx.lineJoin = 'round'
  diamond()
  ctx.fillStyle = BAD_FILL
  ctx.fill()
  ctx.shadowColor = 'transparent'

  ctx.fillStyle = BAD_ICON
  ctx.strokeStyle = BAD_ICON
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  const g = r * 0.42
  switch (kind) {
    case 'narrow':
      // Ракетка со струнами + минус снизу — сжатие зоны отбоя.
      drawRacketIcon(ctx, g * 0.95)
      ctx.lineWidth = g * 0.18
      ctx.beginPath()
      ctx.moveTo(-g * 0.3, g * 0.95)
      ctx.lineTo(g * 0.3, g * 0.95)
      ctx.stroke()
      return
    case 'fast':
      // Молния — «ускорение» читается резче шеврона.
      ctx.beginPath()
      ctx.moveTo(g * 0.18, -g)
      ctx.lineTo(-g * 0.45, g * 0.12)
      ctx.lineTo(-g * 0.02, g * 0.12)
      ctx.lineTo(-g * 0.18, g)
      ctx.lineTo(g * 0.45, -g * 0.12)
      ctx.lineTo(g * 0.02, -g * 0.12)
      ctx.closePath()
      ctx.fill()
      return
    case 'fog':
      // Облако.
      ctx.beginPath()
      ctx.arc(-g * 0.45, g * 0.15, g * 0.45, 0, Math.PI * 2)
      ctx.arc(g * 0.15, -g * 0.15, g * 0.55, 0, Math.PI * 2)
      ctx.arc(g * 0.6, g * 0.2, g * 0.38, 0, Math.PI * 2)
      ctx.rect(-g * 0.45, g * 0.15, g * 1.05, g * 0.45)
      ctx.fill()
      return
    case 'breaker':
      // Крупный «X» — сбой серии.
      ctx.lineWidth = g * 0.3
      ctx.beginPath()
      ctx.moveTo(-g * 0.7, -g * 0.7)
      ctx.lineTo(g * 0.7, g * 0.7)
      ctx.moveTo(g * 0.7, -g * 0.7)
      ctx.lineTo(-g * 0.7, g * 0.7)
      ctx.stroke()
      return
  }
}

/**
 * Туман: верх поля затянут, волан на подлёте прячется в нём — траекторию
 * приходится угадывать. Края мягкие и чуть «дышат», чтобы не было ощущения
 * плоской плашки поверх игры. Плотность нарастает и спадает с остатком эффекта.
 */
export function drawFog(ctx: CanvasRenderingContext2D, left: number, time: number): void {
  const total = gameConfig.bonus.fogDuration
  const fade = Math.min(1, left / 0.6, (total - left) / 0.6)
  const depth = WORLD_HEIGHT * gameConfig.bonus.fogDepth
  const grad = ctx.createLinearGradient(0, 0, 0, depth)
  grad.addColorStop(0, `rgba(236, 242, 250, ${0.96 * fade})`)
  grad.addColorStop(0.75, `rgba(236, 242, 250, ${0.9 * fade})`)
  grad.addColorStop(1, 'rgba(236, 242, 250, 0)')
  ctx.fillStyle = grad
  ctx.fillRect(-WORLD_WIDTH, -WORLD_HEIGHT, WORLD_WIDTH * 3, WORLD_HEIGHT + depth)

  // Клубы по нижней кромке — размытые пятна, а не круги: иначе вместо тумана
  // выходит ряд пузырей.
  for (let i = 0; i < 9; i++) {
    const x = (i / 8) * WORLD_WIDTH + Math.sin(time * 0.8 + i) * 12
    const y = depth * 0.86 + Math.cos(time * 0.6 + i * 1.3) * 8
    const puff = ctx.createRadialGradient(x, y, 0, x, y, 46)
    puff.addColorStop(0, `rgba(236, 242, 250, ${0.5 * fade})`)
    puff.addColorStop(1, 'rgba(236, 242, 250, 0)')
    ctx.fillStyle = puff
    ctx.fillRect(x - 46, y - 46, 92, 92)
  }
}

/** Страховка: светящаяся полоса под ракеткой — видно, что промах простят. */
export function drawShieldLine(ctx: CanvasRenderingContext2D, time: number): void {
  const y = gameConfig.paddle.y + 30
  const alpha = 0.45 + Math.sin(time * 3) * 0.15
  const grad = ctx.createLinearGradient(0, 0, WORLD_WIDTH, 0)
  grad.addColorStop(0, 'rgba(255, 255, 255, 0)')
  grad.addColorStop(0.5, `rgba(255, 255, 255, ${alpha})`)
  grad.addColorStop(1, 'rgba(255, 255, 255, 0)')
  ctx.strokeStyle = grad
  ctx.lineWidth = 3
  ctx.beginPath()
  ctx.moveTo(20, y)
  ctx.lineTo(WORLD_WIDTH - 20, y)
  ctx.stroke()
}
